import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Sparkles, Ticket, Calendar, Clock, Users, ArrowRight, AlertCircle, Loader2, Download, MapPin } from "lucide-react";
import SEOHead from "../components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 6;
const POLL_INTERVAL = 2500;

const fmtTime = (t) => { if (!t) return ""; const [h, m] = t.split(":").map(Number); const ap = h >= 12 ? "pm" : "am"; const hh = ((h + 11) % 12) + 1; return `${hh}:${m.toString().padStart(2, "0")}${ap}`; };

export default function VividBookingSuccess() {
 const [params] = useSearchParams();
 const sessionId = params.get("session_id");
 const [state, setState] = useState({ status: "checking", data: null, error: "" });
 const polledRef = useRef(0);

 useEffect(() => { document.title = "Vivid Cruise Booking | Magic Cruises"; }, []);

 useEffect(() => {
  if (!sessionId) { setState({ status: "error", data: null, error: "No payment session in URL" }); return; }
  let cancelled = false;
  const poll = async () => {
   try {
    // Trust-redirect finalization: Stripe only redirects to success_url when paid.
    // This marks the order paid + decrements inventory idempotently on first visit.
    try { await axios.post(`${API}/checkout/finalize/${sessionId}`); } catch (_) { /* may have already been finalized */ }
    const r = await axios.get(`${API}/checkout/status/${sessionId}`);
    if (cancelled) return;
    const ps = r.data.payment_status;
    if (ps === "paid") { setState({ status: "paid", data: r.data, error: "" }); return; }
    if (r.data.status === "expired") { setState({ status: "expired", data: r.data, error: "Session expired" }); return; }
    polledRef.current += 1;
    if (polledRef.current >= MAX_POLLS) { setState({ status: "timeout", data: r.data, error: "Still processing, check your email" }); return; }
    setTimeout(poll, POLL_INTERVAL);
   } catch (e) {
    if (cancelled) return;
    setState({ status: "error", data: null, error: e?.response?.data?.detail || "Unable to verify payment" });
   }
  };
  poll();
  return () => { cancelled = true; };
 }, [sessionId]);

 const meta = state.data?.metadata || {};
 const order = state.data?.order || {};
 const amount = state.data?.amount_total ? (state.data.amount_total / 100).toFixed(2) : (order.amount ? order.amount.toFixed(2) : "");

 return (
  <main className="bg-[hsl(260,55%,4%)] text-white min-h-screen relative overflow-hidden" data-testid="vivid-booking-success">
   <SEOHead title="Vivid Booking Confirmation | Magic Cruises" description="Vivid Sydney cruise booking confirmation" />
   <div className="absolute inset-0 pointer-events-none">
    <div className="absolute top-0 left-1/4 w-[40rem] h-[40rem] rounded-full bg-fuchsia-500/20 blur-3xl animate-pulse" />
    <div className="absolute bottom-0 right-1/4 w-[40rem] h-[40rem] rounded-full bg-violet-500/15 blur-3xl animate-pulse" style={{ animationDelay: "1.5s" }} />
   </div>

   <div className="relative pt-32 pb-32 px-5 lg:px-10 mx-auto max-w-3xl">
    {state.status === "checking" && (
     <div className="text-center" data-testid="booking-checking">
      <Loader2 className="w-12 h-12 text-fuchsia-300 mx-auto mb-6 animate-spin" />
      <h1 className="text-3xl md:text-4xl font-black mb-3">Confirming your payment…</h1>
      <p className="text-white/65 text-lg">Hold tight, this usually takes a couple of seconds.</p>
     </div>
    )}

    {state.status === "paid" && (
     <div data-testid="booking-paid">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-fuchsia-500 to-violet-600 mx-auto flex items-center justify-center shadow-2xl shadow-fuchsia-500/50 mb-7">
       <Check className="w-10 h-10 text-white" strokeWidth={3} />
      </div>
      <div className="text-center mb-12">
       <div className="text-fuchsia-300 text-[11px] uppercase tracking-[0.32em] font-bold mb-3">Payment Confirmed</div>
       <h1 className="font-display font-black tracking-tight leading-[0.98] mb-5" style={{ fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}>
        See you <span className="vivid-gradient-text italic font-light">on the harbour.</span>
       </h1>
       <p className="text-white/82 text-lg leading-[1.7] max-w-xl mx-auto">
        Your Vivid Sydney cruise tickets are confirmed. We've sent your boarding pass to <strong className="text-white">{meta.customer_email}</strong>.
       </p>
      </div>

      <div className="rounded-3xl ring-1 ring-fuchsia-400/30 bg-gradient-to-br from-fuchsia-600/15 via-violet-600/10 to-sky-600/10 backdrop-blur-md p-7 lg:p-8 mb-8">
       <div className="text-fuchsia-300 text-[11px] uppercase tracking-[0.32em] font-bold mb-5 flex items-center gap-2"><Ticket className="w-3.5 h-3.5" /> Your Ticket</div>
       <dl className="space-y-4">
        <SummaryRow Icon={Calendar} label="Date" value={meta.date ? new Date(meta.date + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : ""} />
        <SummaryRow Icon={Clock} label="Departure" value={meta.time ? `${fmtTime(meta.time)} · Side ${meta.side}` : ""} />
        <SummaryRow Icon={Sparkles} label="Tier" value={meta.tier_name || meta.tier || ""} />
        <SummaryRow Icon={Users} label="Tickets" value={`${meta.adult_qty || 0} adult · ${meta.child_qty || 0} child`} />
        <SummaryRow Icon={MapPin} label="Board at" value="King St Wharf, Darling Harbour · 15 min early" />
       </dl>
       <div className="border-t border-white/15 mt-6 pt-5 flex items-baseline justify-between">
        <span className="text-white/65 uppercase tracking-widest text-[10px] font-bold">Total paid</span>
        <span className="vivid-gradient-text text-3xl font-black">${amount} AUD</span>
       </div>
       <div className="text-white/45 text-[10px] uppercase tracking-widest font-bold text-right">Reference · {state.data?.session_id?.slice(-8) || ""}</div>
      </div>

      <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 mb-8 flex items-start gap-4">
       <Download className="w-5 h-5 text-fuchsia-300 shrink-0 mt-0.5" />
       <div className="flex-1">
        <div className="text-white font-bold mb-1">Your ticket PDF is ready</div>
        <div className="text-white/82 text-sm leading-[1.7]">Boarding pass-style ticket with QR code. Show it on your phone or print it for boarding.</div>
       </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-center">
       <a href={`${API}/tickets/${state.data?.session_id}/pdf`} target="_blank" rel="noopener noreferrer" className="px-7 py-4 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-xs font-black uppercase tracking-[0.22em] shadow-xl shadow-fuchsia-500/40 transition-all hover:scale-[1.02] inline-flex items-center gap-2" data-testid="success-download-pdf">
        <Download className="w-4 h-4" /> Download Ticket PDF
       </a>
       <Link to="/cruises/vivid-cruises-sydney" className="px-7 py-4 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-black uppercase tracking-[0.22em] border border-white/20 transition-colors" data-testid="success-back-vivid">
        Back to Vivid
       </Link>
      </div>
     </div>
    )}

    {state.status === "expired" && (
     <div className="text-center" data-testid="booking-expired">
      <AlertCircle className="w-14 h-14 text-amber-300 mx-auto mb-5" />
      <h1 className="text-3xl md:text-4xl font-black mb-3">Session expired</h1>
      <p className="text-white/65 text-base mb-7">No charge was made. Please try booking again.</p>
      <Link to="/cruises/vivid-cruises-sydney/book" className="inline-flex px-7 py-4 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-xs font-black uppercase tracking-[0.22em] shadow-xl shadow-fuchsia-500/40">Try again</Link>
     </div>
    )}

    {state.status === "timeout" && (
     <div className="text-center" data-testid="booking-timeout">
      <Loader2 className="w-12 h-12 text-fuchsia-300 mx-auto mb-5" />
      <h1 className="text-3xl md:text-4xl font-black mb-3">Still processing</h1>
      <p className="text-white/65 text-base mb-2 max-w-xl mx-auto leading-[1.7]">Your bank is taking longer than usual to confirm. Your email at <strong className="text-white">{meta.customer_email || "the address you provided"}</strong> will receive confirmation as soon as it clears. You can safely close this page.</p>
     </div>
    )}

    {state.status === "error" && (
     <div className="text-center" data-testid="booking-error">
      <AlertCircle className="w-14 h-14 text-red-300 mx-auto mb-5" />
      <h1 className="text-3xl md:text-4xl font-black mb-3">Something went wrong</h1>
      <p className="text-white/65 text-base mb-7">{state.error}</p>
      <Link to="/cruises/vivid-cruises-sydney/book" className="inline-flex px-7 py-4 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-xs font-black uppercase tracking-[0.22em] shadow-xl shadow-fuchsia-500/40">Try again</Link>
     </div>
    )}
   </div>
  </main>
 );
}

function SummaryRow({ Icon, label, value }) {
 return (
  <div className="flex items-start gap-3">
   <Icon className="w-4 h-4 text-fuchsia-300 mt-0.5 shrink-0" />
   <div className="flex-1 flex justify-between gap-3 flex-wrap">
    <span className="text-white/55 uppercase tracking-widest text-[10px] font-bold">{label}</span>
    <span className="text-white font-semibold text-right">{value}</span>
   </div>
  </div>
 );
}
